import type { UrlPreview } from "../../domain/app-types.ts";
import { escapeHtml } from "../../ui/text.ts";

export type UrlPreviewRequest = (path: string, init?: RequestInit) => Promise<Response | null>;

interface PreviewResult { url: string; preview?: UrlPreview; retry?: boolean; message?: string }
const batches = new WeakMap<HTMLElement, { description: string; results: PreviewResult[] }>();

function previewHost(url: string): string {
  try { return new URL(url).host; } catch { return url; }
}

async function fetchPreview(url: string, request: UrlPreviewRequest): Promise<PreviewResult> {
  try {
    const response = await request("/api/v1/url-preview", { signal: AbortSignal.timeout(10_000), method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ url }) });
    if (response?.ok) {
      const preview = await response.json() as UrlPreview;
      if (!preview || typeof preview.title !== "string" || typeof preview.site_name !== "string" || preview.description != null && typeof preview.description !== "string") throw new Error("Invalid preview response");
      return { url, preview };
    }
    if (response?.status === 401 || response?.status === 403) return { url, retry: true, message: "Sign in or check your access, then retry." };
    if (response?.status === 400) {
      const body = await response.json().catch(() => null) as { error?: string } | null;
      const temporary = body?.error === "The page could not be reached" || body?.error === "The preview could not be read";
      return { url, retry: temporary, message: temporary ? "The linked page could not be reached. You can retry." : "Preview unavailable. Private-network, redirecting, non-HTML, or oversized pages cannot be previewed." };
    }
    if (response && response.status >= 400 && response.status < 500 && ![408, 429].includes(response.status)) return { url, message: "Preview unavailable for this link." };
  } catch { /* Network failures and invalid responses can be retried explicitly. */ }
  return { url, retry: true, message: "Could not load this preview. Check your connection and retry." };
}

export function descriptionUrls(description: string): string[] {
  return [...new Set((description.match(/https?:\/\/[^\s<>()]+/g) || []).map(value => value.replace(/[.,;!?]+$/, "")))].slice(0, 5);
}

export async function loadDescriptionUrlPreviews(description: string, button: HTMLElement, request: UrlPreviewRequest): Promise<void> {
  const results = button.closest("section")?.querySelector<HTMLElement>("[data-preview-results]");
  if (!results) return;
  if (button.hasAttribute("disabled")) return;
  const previous = batches.get(button);
  const saved = previous?.description === description ? previous.results : [];
  button.setAttribute("disabled", "");
  results.setAttribute("aria-live", "polite");
  results.setAttribute("aria-busy", "true");
  button.textContent = "Loading previews…";
  if (!saved.length) results.innerHTML = `<p class="muted">Fetching privacy-protected previews…</p>`;
  const loaded = await Promise.all(descriptionUrls(description).map(url => {
    const existing = saved.find(item => item.url === url);
    return existing && !existing.retry ? existing : fetchPreview(url, request);
  }));
  batches.set(button, { description, results: loaded });
  results.innerHTML = loaded.map(({ url, preview, message }) => preview
    ? `<a class="link-preview" href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer"><small>${escapeHtml(preview.site_name)}</small><strong>${escapeHtml(preview.title)}</strong>${preview.description ? `<p>${escapeHtml(preview.description)}</p>` : ""}</a>`
    : `<p class="muted"><a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(previewHost(url))}</a> · ${escapeHtml(message || "Preview unavailable.")}</p>`).join("");
  results.setAttribute("aria-busy", "false");
  const retry = loaded.some(item => item.retry);
  button.textContent = retry ? "Retry failed previews" : loaded.some(item => item.preview) ? "Previews loaded" : "Previews unavailable";
  if (retry) button.removeAttribute("disabled");
}
